import { useCallback, useRef, useState } from 'react';
import { driver } from 'driver.js';
import {
  BASE_DRIVER_CONFIG,
  DRIVER_OVERLAY_Z_INDEX,
  PERSONA_TOURS,
  TOUR_IDS,
} from './onboardingConfig.js';
import { getStepsForTour } from './onboardingSteps.js';
import {
  isTourDone,
  persistTourStatus,
  resetTourProgress,
} from './onboardingStorage.js';
import { prepareTourNavigation, isTourStartable } from './onboardingNavigation.js';
import { waitForSelector } from './onboardingDomUtils.js';
import { emitTourEvent } from './onboardingTourEvents.js';
import { isOwnedDeal } from '../utils/dealPurpose.js';

export { waitForSelector } from './onboardingDomUtils.js';

/**
 * @param {object} params
 * @param {string | undefined} params.uid
 * @param {string | undefined} params.persona
 * @param {(key: string) => string} params.t
 * @param {string} params.currentStep
 * @param {boolean} [params.hasProperties]
 * @param {{ id?: string } | null} [params.currentProperty]
 * @param {{ id?: string } | null} [params.currentScenario]
 * @param {{ id?: string }[]} [params.properties]
 * @param {(step: string) => void} [params.setCurrentStep]
 * @param {(p: object) => void} [params.setCurrentProperty]
 * @param {import('./onboardingStorage.js').TourProgressMap} params.tourProgress
 * @param {(progress: import('./onboardingStorage.js').TourProgressMap) => void} params.setTourProgress
 * @param {(data: object) => Promise<void> | void} [params.updateUserProfile]
 */
export function useOnboarding({
  uid,
  persona,
  t,
  currentStep,
  hasProperties = false,
  currentProperty = null,
  currentScenario = null,
  properties = [],
  setCurrentStep,
  setCurrentProperty,
  tourProgress,
  setTourProgress,
  updateUserProfile,
}) {
  const [activeTour, setActiveTour] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const driverRef = useRef(null);
  const activeTourRef = useRef(null);
  const destroyReasonRef = useRef('none');

  const destroyDriver = useCallback(() => {
    if (driverRef.current) {
      try {
        driverRef.current.destroy();
      } catch {
        /* already destroyed */
      }
      driverRef.current = null;
    }
    setIsRunning(false);
    setActiveTour(null);
    activeTourRef.current = null;
  }, []);

  const markTourEnd = useCallback(
    async (tourId, status) => {
      if (!uid || !tourId) return;
      const next = await persistTourStatus(
        uid,
        tourId,
        status,
        tourProgress,
        updateUserProfile,
      );
      setTourProgress(next);
    },
    [uid, tourProgress, updateUserProfile, setTourProgress],
  );

  const skipTour = useCallback(
    async (tourId) => {
      const id = tourId || activeTourRef.current;
      if (!id) return;
      destroyReasonRef.current = 'skipped';
      emitTourEvent({ tourId: id, action: 'skipped', persona, currentStep });
      destroyDriver();
      await markTourEnd(id, 'skipped');
    },
    [destroyDriver, markTourEnd, persona, currentStep],
  );

  const completeTour = useCallback(
    async (tourId) => {
      const id = tourId || activeTourRef.current;
      if (!id) return;
      destroyReasonRef.current = 'completed';
      destroyDriver();
      await markTourEnd(id, 'completed');
    },
    [destroyDriver, markTourEnd],
  );

  const startTour = useCallback(
    async (tourId, options = {}) => {
      const { force = false } = options;
      if (!uid || !tourId) return false;

      const eligible = PERSONA_TOURS[persona || 'investisseur'] || PERSONA_TOURS.investisseur;
      if (!eligible.includes(tourId)) return false;

      if (!force && isTourDone(tourProgress, tourId)) return false;
      if (isRunning) destroyDriver();

      const navCtx = {
        currentStep,
        currentProperty,
        currentScenario,
        properties,
        setCurrentStep: setCurrentStep || (() => {}),
        setCurrentProperty,
      };

      if (!isTourStartable(tourId, navCtx) && !force) {
        return false;
      }

      const navigated = await prepareTourNavigation(tourId, { ...navCtx, force });
      if (!navigated && tourId !== TOUR_IDS.WELCOME) {
        console.warn('[onboarding] Could not navigate for tour', tourId);
        return false;
      }

      const steps = getStepsForTour(tourId, {
        persona,
        t,
        hasProperties,
        currentStep,
        isOwned: isOwnedDeal(currentProperty),
      });

      if (!steps.length) {
        console.warn('[onboarding] No steps available for tour', tourId);
        return false;
      }

      destroyReasonRef.current = 'none';
      activeTourRef.current = tourId;
      setActiveTour(tourId);
      setIsRunning(true);

      emitTourEvent({
        tourId,
        action: 'started',
        persona,
        currentStep,
      });

      const driverObj = driver({
        ...BASE_DRIVER_CONFIG,
        steps,
        nextBtnText: t('tour.actions.next'),
        prevBtnText: t('tour.actions.previous'),
        doneBtnText: t('tour.actions.done'),
        popoverClass: 'rentalyzer-tour-popover',
        onPopoverRender: (popover, { state }) => {
          const wrapper = popover.wrapper;
          if (!wrapper.classList.contains('rentalyzer-tour-popover-root')) {
            wrapper.classList.add('rentalyzer-tour-popover-root');
          }
          if (!wrapper.querySelector('.rentalyzer-tour-popover-brand')) {
            const brand = document.createElement('div');
            brand.className = 'rentalyzer-tour-popover-brand';
            brand.setAttribute('aria-hidden', 'true');
            wrapper.insertBefore(brand, wrapper.firstChild);
          }
          if (popover.title?.textContent) {
            popover.title.classList.add('rentalyzer-tour-popover-title');
          }
          if (popover.description?.textContent) {
            popover.description.classList.add('rentalyzer-tour-popover-body');
          }
          if (popover.progress) {
            popover.progress.style.display = 'none';
          }
          const isFirstStep = state.activeIndex === 0;
          if (popover.previousButton) {
            popover.previousButton.style.display = isFirstStep ? 'none' : '';
          }
          const isIntro = !state.activeStep?.element;
          wrapper.classList.toggle('rentalyzer-tour-popover--intro', isIntro);
        },
        onDestroyed: () => {
          driverRef.current = null;
          setIsRunning(false);
          setActiveTour(null);
          const endedTour = activeTourRef.current;
          activeTourRef.current = null;

          if (!endedTour) return;

          if (destroyReasonRef.current === 'skipped') return;
          if (destroyReasonRef.current === 'completed') return;

          // User closed via overlay / escape on a non-final step → treat as skipped
          if (!isTourDone(tourProgress, endedTour)) {
            markTourEnd(endedTour, 'skipped');
          }
        },
        onNextClick: (_el, _step, { driver: d, state }) => {
          emitTourEvent({
            tourId,
            action: 'step_next',
            stepIndex: state.activeIndex,
            persona,
            currentStep,
          });
          const isLast =
            typeof state.activeIndex === 'number' &&
            state.activeIndex >= steps.length - 1;
          if (isLast) {
            destroyReasonRef.current = 'completed';
            d.destroy();
            emitTourEvent({ tourId, action: 'completed', persona, currentStep });
            markTourEnd(tourId, 'completed');
          } else {
            d.moveNext();
          }
        },
        onCloseClick: () => {
          skipTour(tourId);
        },
      });

      driverRef.current = driverObj;

      requestAnimationFrame(() => {
        const overlay = document.querySelector('.driver-overlay');
        if (overlay) {
          overlay.classList.add('rentalyzer-driver-overlay');
          overlay.style.zIndex = String(DRIVER_OVERLAY_Z_INDEX);
        }
        driverObj.drive();
      });

      return true;
    },
    [
      uid,
      persona,
      t,
      hasProperties,
      currentStep,
      currentProperty,
      currentScenario,
      properties,
      setCurrentStep,
      setCurrentProperty,
      tourProgress,
      isRunning,
      destroyDriver,
      skipTour,
      markTourEnd,
    ],
  );

  const resumeTour = useCallback(
    (tourId) => startTour(tourId, { force: true }),
    [startTour],
  );

  const canStartTour = useCallback(
    (tourId) => isTourStartable(tourId, {
      currentStep,
      currentProperty,
      currentScenario,
      properties,
      setCurrentStep: setCurrentStep || (() => {}),
      setCurrentProperty,
    }),
    [currentStep, currentProperty, currentScenario, properties, setCurrentStep, setCurrentProperty],
  );

  const resetTour = useCallback(
    async (tourId) => {
      if (!uid) return;
      const next = await resetTourProgress(uid, tourId, tourProgress, updateUserProfile);
      setTourProgress(next);
    },
    [uid, tourProgress, updateUserProfile, setTourProgress],
  );

  /** @type {import('./onboardingConfig.js').TourId} */
  const suggestedTourId = TOUR_IDS.WELCOME;

  return {
    activeTour,
    isRunning,
    tourProgress,
    startTour,
    skipTour,
    completeTour,
    resumeTour,
    resetTour,
    destroyDriver,
    suggestedTourId,
    waitForSelector,
    canStartTour,
  };
}
